var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { ETemplateType } from "../redux/template/template.types";
import { canRegisterInspectionReport, getContractDocument, } from "../reduxV2/rentRegistration/rentRegistration.utils";
import { EContractMemberTypes, ELeaseType } from "../types/contract.types";
import { EDocumentCategory } from "../types/document.types";
import { EDocumentPackageTypes } from "../types/documentPackage.types";
import { EPropertyTypeId } from "../types/property.types";
import { ERegistrationContractDestination, ERegistrationMemberType, } from "../types/registration.types";
import { hasBrusselsRegistration } from "./property.utils";
import { getMembersWithOneOfRoles } from "./roles.utils";
export var EAutoRegistrationKind;
(function (EAutoRegistrationKind) {
    EAutoRegistrationKind["Contract"] = "contract";
    EAutoRegistrationKind["Pir"] = "pir";
})(EAutoRegistrationKind || (EAutoRegistrationKind = {}));
export var EAutoRegistrationDisabledReason;
(function (EAutoRegistrationDisabledReason) {
    EAutoRegistrationDisabledReason["ExistingInspectionReport"] = "existing_inspection_report";
    EAutoRegistrationDisabledReason["ExistingRegistration"] = "existing_registration";
    EAutoRegistrationDisabledReason["MissingContractBarCode"] = "missing_contract_bar_code";
    EAutoRegistrationDisabledReason["MissingDates"] = "missing_dates";
    EAutoRegistrationDisabledReason["RegistrationUnknown"] = "registration_unknown";
})(EAutoRegistrationDisabledReason || (EAutoRegistrationDisabledReason = {}));
export var EAutoRegistrationSubmitAction;
(function (EAutoRegistrationSubmitAction) {
    /** Create a registration for the lease contract and link it to the package. */
    EAutoRegistrationSubmitAction["Create"] = "create";
    /** Opted in from a state the step never offers the opt-in enabled in. */
    EAutoRegistrationSubmitAction["DropOptIn"] = "drop_opt_in";
    /** Link the lease's existing registration, which is what a report attaches to. */
    EAutoRegistrationSubmitAction["LinkExisting"] = "link_existing";
    /** No opt-in: the package carries no registration at all. */
    EAutoRegistrationSubmitAction["None"] = "none";
})(EAutoRegistrationSubmitAction || (EAutoRegistrationSubmitAction = {}));
/**
 * Which registration question - if any - the signing wizard should ask.
 *
 * The package type alone cannot answer this: a property inspection report
 * generated from a Rentio template signs as a CONTRACT package
 * (schema.createDocumentPackage.utils.ts, ETemplateType.PropertyInspection ->
 * EDocumentPackageTypes.Contract), so the signal is the source document's
 * category / the template's type, never the package type on its own.
 */
export var getAutoRegistrationKind = function (params) {
    var documentPackageType = params.documentPackageType, contractId = params.contractId, leaseType = params.leaseType, property = params.property, sourceDocumentCategoryId = params.sourceDocumentCategoryId, sourceDocumentContractId = params.sourceDocumentContractId, templateDocumentType = params.templateDocumentType;
    // Brussels main-residence and student leases need the full Brussels field set
    // and get no question at all in this version. An unresolved property is
    // treated the same way: never offer the question on data we do not have.
    if (!property || !leaseType) {
        return;
    }
    if (hasBrusselsRegistration(property, leaseType)) {
        return;
    }
    var isInspectionReport = sourceDocumentCategoryId === EDocumentCategory.LocationDescription ||
        templateDocumentType === ETemplateType.PropertyInspection;
    if (isInspectionReport) {
        // The inspection report is registered against a lease, so one has to be
        // resolvable - either picked in the wizard or carried by the document.
        return contractId || sourceDocumentContractId
            ? EAutoRegistrationKind.Pir
            : undefined;
    }
    if (documentPackageType !== EDocumentPackageTypes.Contract || !contractId) {
        return;
    }
    // Addendum registration is out of scope for this ticket.
    if (sourceDocumentCategoryId === EDocumentCategory.ContractAddendum) {
        return;
    }
    return EAutoRegistrationKind.Contract;
};
/**
 * Why the opt-in is shown but disabled, if it is.
 *
 * For a lease contract: MyRent needs both dates, and a lease can only carry one
 * registration.
 *
 * For an inspection report neither of those applies: it is attached to the
 * lease's existing registration instead of creating one, so a registration is
 * what it needs rather than what blocks it, and the dates were MyRent's to
 * check when that contract was registered. What it needs instead is the gate
 * the manual registration drawer attaches under - the barcode MyRent hands
 * back for the registered contract, and a report slot that is still open. A
 * lease registered by hand outside Rentio has a registration row and no
 * barcode, and does not qualify.
 */
export var getAutoRegistrationDisabledReason = function (params) {
    var _a;
    var kind = params.kind, contract = params.contract, registration = params.registration;
    if (kind === EAutoRegistrationKind.Pir) {
        // The lease names a registration the wizard has not got: the fetch is in
        // flight, or it failed. Nothing about the barcode is known yet, so say that
        // rather than tell the broker to register a lease that may well be
        // registered already.
        if ((contract === null || contract === void 0 ? void 0 : contract.rentRegistrationId) != null && !registration) {
            return EAutoRegistrationDisabledReason.RegistrationUnknown;
        }
        // No registration to read at all means no barcode either.
        if (!registration || !((_a = getContractDocument(registration)) === null || _a === void 0 ? void 0 : _a.barCode)) {
            return EAutoRegistrationDisabledReason.MissingContractBarCode;
        }
        // The other half of the gate the manual drawer attaches under: the report
        // slot has to be one it would attach to - absent, disapproved or failed.
        // Any other status means MyRent already has a report, is working on one, or
        // the broker settled it outside Rentio.
        return canRegisterInspectionReport(registration)
            ? undefined
            : EAutoRegistrationDisabledReason.ExistingInspectionReport;
    }
    if ((contract === null || contract === void 0 ? void 0 : contract.rentRegistrationId) != null) {
        return EAutoRegistrationDisabledReason.ExistingRegistration;
    }
    if (!(contract === null || contract === void 0 ? void 0 : contract.startDate) || !(contract === null || contract === void 0 ? void 0 : contract.stopDate)) {
        return EAutoRegistrationDisabledReason.MissingDates;
    }
    return;
};
/**
 * What the signing wizard has to do with the broker's answer.
 *
 * Only the lease contract needs a registration created; an inspection report is
 * attached to the one the lease already has. Anything else with an opt-in on it
 * is an opt-in the step would not have offered - the wizard keeps each step's
 * last submitted values, so an answer given for an eligible lease survives a
 * switch to one that cannot be registered until the step is submitted again.
 */
export var getAutoRegistrationSubmitAction = function (params) {
    var isOptedIn = params.isOptedIn, kind = params.kind, disabledReason = params.disabledReason, contractId = params.contractId, propertyId = params.propertyId;
    if (!isOptedIn) {
        return { action: EAutoRegistrationSubmitAction.None };
    }
    if (disabledReason || !contractId || propertyId == null) {
        return { action: EAutoRegistrationSubmitAction.DropOptIn };
    }
    switch (kind) {
        case EAutoRegistrationKind.Contract:
            return {
                action: EAutoRegistrationSubmitAction.Create,
                contractId: contractId,
                propertyId: propertyId,
            };
        case EAutoRegistrationKind.Pir:
            return { action: EAutoRegistrationSubmitAction.LinkExisting };
        default:
            return { action: EAutoRegistrationSubmitAction.DropOptIn };
    }
};
/**
 * The parties that end up on the registration, derived from the contract
 * members exactly as the manual wizard does. When the broker signs on behalf
 * of the owners the broker is the lessor and the owners are not sent.
 */
export var getRegistrationMembersForSigning = function (params) {
    var members = params.members, signAsBrokerAccountId = params.signAsBrokerAccountId;
    // The id comes from a form value the caller coerces with `+`, so a
    // non-numeric answer arrives as NaN - which `!= null` lets through and would
    // send `{ role: Broker, accountId: NaN }` while dropping the owners. An id
    // that is not a usable number cannot name a broker, so both legs below read
    // this instead. 0 is a legal account id and stays one.
    var brokerAccountId = Number.isFinite(signAsBrokerAccountId)
        ? signAsBrokerAccountId
        : undefined;
    var accountIdsWithRole = function (roles) {
        return getMembersWithOneOfRoles(members, roles).map(function (member) { return member.account.id; });
    };
    var owners = brokerAccountId != null
        ? []
        : accountIdsWithRole([EContractMemberTypes.Owner]).map(function (accountId) { return ({
            role: ERegistrationMemberType.Owner,
            accountId: accountId,
        }); });
    var broker = brokerAccountId != null
        ? [
            {
                role: ERegistrationMemberType.Broker,
                accountId: brokerAccountId,
            },
        ]
        : [];
    var tenants = accountIdsWithRole([
        EContractMemberTypes.Tenant,
        EContractMemberTypes.Parent,
    ]).map(function (accountId) { return ({
        role: ERegistrationMemberType.Tenant,
        accountId: accountId,
    }); });
    return __spreadArray(__spreadArray(__spreadArray([], owners, true), broker, true), tenants, true);
};
/**
 * The accounts behind the parties the step shows, for the ones it can resolve.
 *
 * Every source here is typed as always carrying a nested `account`
 * (`IMember.account`, `IContact.account`), and none of them is guaranteed to:
 * `GET /brokers/:brokerId/contact/:customId` - which is what loads the signing
 * wizard's legal contact - returns `accountId` and no `account` at all. The
 * types do not admit that, so the candidates are widened deliberately and
 * filtered once, here, where all three sources converge.
 *
 * Leaving a party out is safe: the list is rendered from the party account ids,
 * and ContactFetchListItem resolves each one to a contact by id on its own.
 */
export var getRegistrationPartyAccounts = function (params) {
    var contractMembers = params.contractMembers, legalContact = params.legalContact, contactMeMaster = params.contactMeMaster, partyAccountIds = params.partyAccountIds;
    var candidates = __spreadArray(__spreadArray([], (contractMembers || []).map(function (member) { return member.account; }), true), [
        legalContact === null || legalContact === void 0 ? void 0 : legalContact.account,
        contactMeMaster === null || contactMeMaster === void 0 ? void 0 : contactMeMaster.account,
    ], false);
    return candidates.filter(function (account) {
        return !!account && partyAccountIds.includes(account.id);
    });
};
/**
 * The destination MyRent gets, derived from the lease type and the property
 * type. This is the derivation the manual registration wizard has always used
 * (schema.registration.utils.ts): a commercial property type wins over the
 * lease type, student housing stays `housing` - the `university` destination
 * was considered and deliberately left out.
 */
export var getDefaultRegistrationDestination = function (params) {
    var leaseType = params.leaseType, propertyTypeId = params.propertyTypeId;
    if (propertyTypeId === EPropertyTypeId.Commercial) {
        return ERegistrationContractDestination.Commercial;
    }
    if (leaseType === ELeaseType.CommercialLease) {
        return ERegistrationContractDestination.Commercial;
    }
    return ERegistrationContractDestination.Housing;
};
/** MyRent only accepts a withholding tax answer for a non-housing lease. */
export var isPropertyTaxRelevantForDestination = function (destination) { return !!destination && destination !== ERegistrationContractDestination.Housing; };
export var buildAutoRegistrationCreateBody = function (params) {
    var contractId = params.contractId, propertyId = params.propertyId, members = params.members, destination = params.destination, language = params.language, propertyTaxPaidByTenant = params.propertyTaxPaidByTenant;
    return {
        contractId: contractId,
        propertyId: propertyId,
        members: members,
        documents: [],
        rentContractDestination: destination,
        preferredContractLanguage: language,
        rentContractPropertyTaxPaidByTenant: isPropertyTaxRelevantForDestination(destination) &&
            propertyTaxPaidByTenant === "yes",
    };
};
